"use client";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import * as d3 from "d3";
import { cn } from "@/utils/helpers";
import { getLang } from "@/utils/selectors";
import ChartTooltip from "./components/ChartTooltip";
import styles from "./Chart.module.css";

export interface ChartDatumProps {
	x: Date;
	y: number;
};

export interface ChartProps {
	data: ChartDatumProps[];
	unit: string;
	year: number;
	className?: string;
};

export default function Chart({
	data,
	unit,
	year,
	className,
} : ChartProps) {
	const id = useId();
	const ref = useRef<HTMLDivElement>(null);
	const svgRef = useRef<SVGSVGElement>(null);
	const [hoveredChartPoint, setHoveredChartPoint] = useState<any>()
	const [hoveredChartDatum, setHoveredChartDatum] = useState<any>()

	const buildChart = useCallback(() => {
		if (!data || data.length === 0 || !ref.current || !svgRef.current) return;
		const margin = {
			top: 10,
			right: 30,
			bottom: 30,
			left: 40
		};
		const width = ref.current.getBoundingClientRect().width;
		const height = ref.current.getBoundingClientRect().height;
		const innerWidth = width - margin.left - margin.right;
		const innerHeight = height - margin.top - margin.bottom;
		// Clear previous chart before redrawing
		d3.select(svgRef.current).selectAll("*").remove();
		// Set SVG size and add group
		const svg = d3.select(svgRef.current)
			.attr("width", width)
			.attr("height", height)
			.append("g")
			.attr("transform", `translate(${margin.left},${margin.top})`);
		// Create X axis scale
		const xScale = d3.scaleTime()
			.domain(d3.extent(data, (d) => d.x) as [Date, Date])
			.range([0, innerWidth]);
		// Create array of all X points
		const xPositions = data.map((d: ChartDatumProps) => xScale(d.x));
		// Create Y axis scale
		const yScale = d3.scaleLinear()
			.domain([0, d3.max(data, (d: ChartDatumProps) => d.y) ?? 0])
			.range([innerHeight, 0]);
		// Create X axis nodes
		svg.append("g")
			.attr("transform", `translate(0,${innerHeight})`)
			.call(
				d3.axisBottom(xScale)
					.ticks(d3.timeYear.every(
						width > 300
							? 2
							: width > 100
								? 3
								: 5
					))
			);
		// Create Y axis nodes
		svg.append("g")
			.call(
				d3.axisLeft(yScale)
					.tickSize(2)
			)
			.append("text")
				.attr("class", `label ${unit}`)
				.text(getLang("unit", unit))
				.attr("text-anchor", "middle")
				.attr("transform", "rotate(-90)")
				.attr("x", -((innerHeight) / 2))
				.attr("y", -28);
		// Add indicator line if year argument found in data
		const activeDatum = data.find((d: ChartDatumProps) => d.x.getFullYear() === year);
		if(activeDatum) {
			svg.append("line")
				.attr("x1", xScale(activeDatum.x))
				.attr("x2", xScale(activeDatum.x))
				.attr("y1", innerHeight)
				// .attr("y2", yScale(activeDatum.y))
				.attr("y2", 0)
				.attr("stroke", "var(--color-smoke-choropleth-6)")
				.attr("stroke-width", 2)
				.attr("stroke-dasharray", "4,4");
		}
		// Add line connecting data points
		svg.append("path")
			.datum(data)
			.attr("fill", "none")
			.attr("stroke", "var(--color-primary)")
			.attr("stroke-width", 1.5)
			.attr("d", d3.line<ChartDatumProps>()
				.x((d: ChartDatumProps) => xScale(d.x))
				.y((d: ChartDatumProps) => yScale(d.y))
			);
		// Add circles at data points
		svg.selectAll("circle")
			.data(data)
			.enter()
			.append("circle")
			.attr("cx", (d: ChartDatumProps) => xScale(d.x))
			.attr("cy", (d: ChartDatumProps) => yScale(d.y))
			.attr("r", 3)
			.attr("fill", "var(--color-primary)");
		// Create buffer rectangles to trigger hover of circles
		const bufferPadding = 5;
		const circleBuffer = data.map((d: ChartDatumProps, i: number) => ({
			start: i === 0
				? -bufferPadding
				: (xPositions[i - 1] + xPositions[i]) / 2,
			end: i === data.length - 1
				? innerWidth + bufferPadding
				: (xPositions[i] + xPositions[i + 1]) / 2
		}));
		// Add buffer rectangles
		svg.append("g")
			.attr("fill", "transparent")
			.attr("pointer-events", "all")
			.selectAll("rect")
			.data(data)
			.join("rect")
			.attr("x", (d: ChartDatumProps, i: number) => circleBuffer[i].start)
			.attr("y", -bufferPadding)
			.attr("width", (d: ChartDatumProps, i: number) => circleBuffer[i].end - circleBuffer[i].start)
			.attr("height", innerHeight + bufferPadding * 2)
			.on("mouseover", (event: any, d: ChartDatumProps) => {
				const svgRect = svgRef.current!.getBoundingClientRect();
				setHoveredChartPoint({
					x: svgRect.left + margin.left + xScale(d.x),
					y: svgRect.top + margin.top + yScale(d.y),
				})
				setHoveredChartDatum({
					year: d.x?.getFullYear(),
					value: d.y
				})
				svg.selectAll("circle")
					.filter((elem: any) => elem.x === d.x)
					.transition()
					.duration(200)
					.attr("r", 5)
			})
			.on("mouseout", (event: any, d: ChartDatumProps) => {
				setHoveredChartDatum(null)
				svg.selectAll("circle")
					.filter((elem: any) => elem.x === d.x)
					.transition()
					.duration(200)
					.attr("r", 3)
			});
	}, [data, unit, year, ref, svgRef]);

	useEffect(() => {
		buildChart();
		const handleResize = () => buildChart();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [buildChart]);

	return (
		<div
			id={id}
			ref={ref}
			className={cn(
				styles.Chart,
				className
			)}
		>
			<svg
				ref={svgRef}
			>
			</svg>
			<ChartTooltip
				x={hoveredChartPoint?.x}
				y={hoveredChartPoint?.y}
				year={hoveredChartDatum?.year}
				value={hoveredChartDatum?.value}
			/>
		</div>
	);
}